'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { c } from '../../../utils/tailwind-merge';
import { FormModal } from '../../molecules/FormModal';
import { WriteReviewTips } from '../../molecules/WriteReviewTips';
import {
  MAX_TITLE_CHARS,
  ReviewFormData,
  WriteReviewModalProps,
  WriteReviewModalPropsMicrocopy,
} from './WriteReviewModal.types';

import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { interpolateStringWithData } from 'utils';
import { InputV2 } from '../../atoms/Input.v2';
import { Textarea } from '../../atoms/Textarea';
import { Banner } from '../../molecules/Banner';
import { StarsV2, StarsV2Props } from '../../molecules/Stars.v2';
import { MAX_FEEDBACK_CHARS } from '../FeedbackModal';

export const emptyWriteReview: WriteReviewModalPropsMicrocopy = {
  title: '',
  editionTitle: '',
  version: '',
  ownedDate: '',
  rateExperience: '',
  titleReview: '',
  titleReviewPlaceholder: '',
  writeReview: '',
  writeReviewPlaceholder: '',
  tips: {
    title: '',
    doTip: {
      title: '',
      content: '',
    },
    dontTip: {
      title: '',
      content: '',
    },
    cta: '',
    expand: '',
  },
  cancel: '',
  submit: '',
  editionSubmit: '',
  close: '',
  remaining: '',
  ratingValidationMessage: '',
  reviewValidationMessage: '',
  remainingTitle: '',
  titleValidationMessage: '',
  multiPackageAsset: '',
};

export const WriteReviewModal = ({
  microcopy,
  onSubmitReview,
  onEmailPublisher,
  title,
  productId,
  data,
  version,
  ownedDate,
  imageUrl,
  reviewModalTrigger,
  isMultiPackage,
  className = '',
}: WriteReviewModalProps) => {
  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const [showServerMessage, setShowServerMessage] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Freeze values to avoid FOUC on editing
  const [frozenValues, setFrozenValues] = useState<Partial<ReviewFormData>>({});

  const serverMessageRef = useRef<HTMLDivElement>(null);

  const reviewFormSchema = z.object({
    // id is not validated as it's pre-filled and not user-editable
    id: z.any(),
    rating: z.number().min(1, { message: microcopy.ratingValidationMessage }),
    title: z.string().min(1, { message: microcopy.titleValidationMessage }),
    body: z.string().min(1, { message: microcopy.reviewValidationMessage }),
    // productMetadata is not validated as it's pre-filled and not user-editable
    productMetadata: z.any(),
    // lastUpdatedAt is not validated as it's pre-filled and not user-editable
    lastUpdatedAt: z.any(),
  });

  const defaultFormValues = useMemo(
    () => ({
      id: data?.id,
      rating: data?.rating || 0,
      title: data?.title || '',
      body: data?.body || '',
      productMetadata: data?.productMetadata || {
        upm: {
          packageVersion: version || '',
        },
      },
      lastUpdatedAt: data?.lastUpdatedAt || '',
    }),
    [data, version]
  );

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    reset,
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewFormSchema),
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (isModalOpen) {
      reset({
        ...defaultFormValues,
        id: data?.id,
      });
    }
  }, [isModalOpen, data, reset, defaultFormValues]);

  const onFormSubmit = async (formData: ReviewFormData) => {
    if (submitIsLoading) return;
    setShowServerMessage(false);
    setSubmitIsLoading(true);

    setFrozenValues({
      title: formData.title,
      body: formData.body,
      rating: formData.rating,
    });

    try {
      if (!productId) {
        throw new Error('Product ID is required to submit a review.');
      }
      await onSubmitReview(productId, formData);
      reset();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Submit review error:', error);
      setShowServerMessage(true);
      setFrozenValues({});
    } finally {
      setSubmitIsLoading(false);
    }
  };

  const handleOnCancelReview = () => {
    reset();
    setIsModalOpen(false);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setShowServerMessage(false);
      setSubmitIsLoading(false);
    }
  };

  const handleOnDismissReview = () => {
    setShowServerMessage(false);
  };

  const renderSectionHeader = (title: string) => (
    <h3 className="body mb-2 w-full font-medium tracking-tighter text-grey-900">
      {title}
    </h3>
  );

  const starProps = (
    field: ControllerRenderProps<ReviewFormData, 'rating'>
  ): StarsV2Props => ({
    editable: true,
    variant: 'blue',
    ariaLabel: microcopy.rateExperience,
    value: field.value,
    onChange: (value: number) => field.onChange(value),
    max: 5,
  });

  const displayTitle = submitIsLoading ? (frozenValues.title ?? '') : undefined;
  const displayBody = submitIsLoading ? (frozenValues.body ?? '') : undefined;

  const isEditing = Boolean(data?.id && data?.lastUpdatedAt);

  const modalContent = (
    <div className="flex flex-col py-4">
      {showServerMessage && (
        <div ref={serverMessageRef} className="mb-5">
          <Banner
            title={microcopy.serverMessage?.title || ''}
            description={microcopy.serverMessage?.description || ''}
            dismiss={microcopy.serverMessage?.dismiss || ''}
            onDismiss={handleOnDismissReview}
            cta={microcopy.serverMessage?.cta || ''}
            style="fill"
            data-test="server-message-alert"
          />
        </div>
      )}
      <figure className="mb-5 flex [&_p]:mb-0 lg:[&_p]:mb-1">
        <Image
          src={imageUrl}
          alt={title}
          aria-hidden="true"
          className="mr-5 h-20 w-20 rounded-lg"
          width={80}
          height={80}
        />
        <figcaption>
          <p className="text-sm font-medium lg:text-md lg:tracking-tighter">
            {title}
          </p>
          <p className="text-sm text-grey-600">
            {isMultiPackage
              ? microcopy.multiPackageAsset
              : interpolateStringWithData(microcopy.version, { version })}
          </p>

          {ownedDate && (
            <p className="text-sm text-grey-600">
              {interpolateStringWithData(microcopy.ownedDate, {
                date: ownedDate,
              })}
            </p>
          )}
        </figcaption>
      </figure>
      <div className="mb-2 flex flex-col lg:mb-2 lg:flex-row">
        <div className="mr-0 lg:mr-8 lg:w-2/3">
          {renderSectionHeader(microcopy.rateExperience)}
          <Controller
            control={control}
            name="rating"
            render={({ field }) => (
              <>
                <StarsV2
                  {...starProps(field)}
                  height={24}
                  width={24}
                  className="mb-5 hidden items-start lg:block"
                />
                <StarsV2
                  {...starProps(field)}
                  height={48}
                  width={48}
                  className="mb-5 block items-start lg:hidden"
                />
              </>
            )}
          />
          {renderSectionHeader(microcopy.titleReview)}
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <InputV2
                id="review-title"
                data-test="review-title"
                data-testid="review-title"
                placeholder={microcopy.titleReviewPlaceholder}
                {...field}
                value={displayTitle ?? field.value}
                showRemainingChars
                maxLength={MAX_TITLE_CHARS}
                aria-invalid={Boolean(errors.title)}
                className="h-10 w-full flex-row"
                wrapperClassName="mb-5"
              />
            )}
          />
          {renderSectionHeader(microcopy.writeReview)}
          <Controller
            control={control}
            name="body"
            render={({ field }) => (
              <Textarea
                id="write-review-textarea"
                data-testid="write-review-textarea"
                data-test="write-review-textarea"
                placeholder={microcopy.writeReviewPlaceholder}
                value={displayBody ?? field.value}
                onChange={(e) => field.onChange(e.target.value)}
                maxLength={MAX_FEEDBACK_CHARS}
                className="h-full min-h-28 w-full lg:min-h-96"
                wrapperClassName="mb-5"
                aria-invalid={Boolean(errors.body)}
                name={field.name}
                showRemainingChars
              />
            )}
          />
        </div>
        <div className="mt-6 lg:mt-0 lg:w-1/3">
          <WriteReviewTips
            {...microcopy.tips}
            onEmailPublisher={onEmailPublisher}
          />
        </div>
      </div>
    </div>
  );

  return (
    <FormModal
      trigger={reviewModalTrigger}
      content={modalContent}
      title={isEditing ? microcopy.editionTitle : microcopy.title}
      close={microcopy.close}
      submit={isEditing ? microcopy.editionSubmit : microcopy.submit}
      disabled={!isValid || submitIsLoading}
      open={isModalOpen}
      onOpenChange={handleModalOpenChange}
      cancel={microcopy.cancel}
      onSubmit={handleSubmit(onFormSubmit)}
      onCancel={handleOnCancelReview}
      className={c('max-w-screen-lg bg-white', className)}
      isStickyFooter={false}
    />
  );
};
